// Import dependencies and libraries
import React from "react";
import { Table, OverlayTrigger, Tooltip, FormCheck } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Import custom tools
import { validateUserRoleIds } from "../../global/GlobalTools";

/**
 * Description: Component to show students attendance in a table
 * @param {boolean} allStudentsSelected
 * @param {function} selectAllStudents
 * @param {array} studentsAttendance
 * @param {function} selectStudent
 * @returns {JSX.Element} StudentsAttendanceTableLegacy component
 */
const StudentsAttendanceTableLegacy = ({
  allStudentsSelected,
  selectAllStudents,
  studentsAttendance,
  selectStudent,
}) => {
  const { t } = useTranslation("dashboard");

  const customStyles = {
    checkBox: {
      width: "16px",
      height: "16px",
    },
    overflowScrollY: {
      overflowY: "scroll",
      overflowX: "hidden",
    },
  };

  /**
   * Description: Render the students attendance table
   * @returns {JSX.Element} StudentsAttendanceTableLegacy component
   */
  return (
    <div>
      <Table
        bordered
        responsive
        style={{
          borderTop: "white",
          borderBottom: "solid 1px #dee2e6",
        }}
      >
        <thead
          className="mt-2 mb-2"
          style={{ borderBottom: "solid 1px #dee2e6" }}
        >
          <tr>
            <th width="89.5%">{t("textStudents.studentName")}</th>
            <th width="10%">
              <div className="d-flex justify-content-center">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip className="color-white" id="selectAllClasses">
                      {t("textStudents.selectAll")}
                    </Tooltip>
                  }
                >
                  <FormCheck
                    aria-label="selectAllStudents"
                    checked={allStudentsSelected}
                    onChange={selectAllStudents}
                    style={customStyles.checkBox}
                  />
                </OverlayTrigger>
              </div>
            </th>
          </tr>
        </thead>
        <tbody style={{ borderTop: "1px" }}>
          <tr>
            <td colSpan="2" style={{ padding: 0 }}>
              <div
                style={{
                  ...customStyles.overflowScrollY,
                  maxHeight: "221px",
                }}
                className="scrollbar-hidden"
              >
                <Table bordered responsive style={{ marginBottom: 0 }}>
                  <tbody>
                    {studentsAttendance.map((student) => {
                      return (
                        <tr
                          key={student.student_id}
                          className="mb-2"
                          style={{ borderWidth: "0px 0" }}
                        >
                          <td width="90%" style={{ borderLeft: "none" }}>
                            {/* If the user is a teacher, they can access the student profile from the dashboard */}
                            {validateUserRoleIds(7) ? (
                              <a
                                href={`${process.env.REACT_APP_V1}/estudiante/${student.student_id}`}
                              >
                                {`${student.student_name} - `}
                                {student.program_name
                                  ? `(${student.program_name} ${student.program_level})`
                                  : `(${student.grade}${student.group})`}
                              </a>
                            ) : (
                              `${student.student_name} - ` +
                              (student.program_name
                                ? `(${student.program_name} ${student.program_level})`
                                : `(${student.grade}${student.group})`)
                            )}
                          </td>
                          <td width="10%" style={{ borderRight: "none" }}>
                            <div className="d-flex justify-content-center">
                              <FormCheck
                                aria-label="selectStudent"
                                checked={student.hasAttendance}
                                onChange={() =>
                                  selectStudent(
                                    student.student_id,
                                    !student.hasAttendance,
                                  )
                                }
                                style={customStyles.checkBox}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

StudentsAttendanceTableLegacy.propTypes = {
  allStudentsSelected: PropTypes.bool.isRequired,
  selectAllStudents: PropTypes.func.isRequired,
  studentsAttendance: PropTypes.array.isRequired,
  selectStudent: PropTypes.func.isRequired,
};

export default StudentsAttendanceTableLegacy;
